import {
    Button,
    LoadingDots,
    ModalBody,
    ModalDialog,
    ModalFooter,
    ModalHeader,
    ModalOverlay
} from '@flos/react-ui';
import useEditCoverablePrice from '../../../hooks/useEditCoverablePrice';
import AttributeChangeInformationComponent from './EditCoverableAttribute/AttributeChangeInformation';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { opcInfoAtom } from '../../../recoil/atoms/opcInfoAtom';
import getEditAttributeConfig from '../config/EditAttribute';
import { getEditCoverableKey } from '../utils/editAttributeUtil';
import { useHippoText } from '../../../hooks/useHippoText';
import usePageNavigation from '../../../hooks/usePageNavigation';
import { jobAtom } from '../../../recoil/atoms/jobAtom';

import type { EditCoverableAttributesDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc';
import type { OPCAttribute, OPCIdentifier } from '../../../recoil/types/opcTypes';
import type {
    CatOpcDTO,
    DogOpcDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc/pet';
import HighlightInfoForCoverage from './HighlightInfoForCoverage';
import type {
    AreaOfDestination_Top,
    ACCContractType_Top,
    ACCFulltimeOrLeisure_Top,
    DogClub_Top,
    MVPYearlyMileage_Top,
    YesNo_Top
} from 'gw-edge-client/dist/types/dto/pc/typekey';
import type { HandleCloseOPCModal, OpcCoverableAttribute } from '../types';
import type { ContentOpcDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc/content';
import type { CarOpcDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc/motor';
import type { TravelOpcDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc/travel';
import type { AdultAccidentOpcDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc/accident';
import { isOpcDataFilledAtom } from '../../../recoil/atoms/isOpcDataFilledAtom';
import { isOpcFetchingAtom } from '../../../recoil/atoms/isOpcFetchingAtom';
import { callMeVariantOpcAtom } from '../../../recoil/atoms/callMeVariantOpcAtom';
import { track } from '../../../utils/trackingUtil';
import useOpcJob from '../../../hooks/useOpcJob';

const generateEditCoverableDTO = (opcInfo: OPCIdentifier): EditCoverableAttributesDTO => {
    const { coverableId, productName, attribute, attributeValue } = opcInfo;
    const coverableAttributeMap: Record<
        OPCAttribute,
        | { dog: DogOpcDTO }
        | { cat: CatOpcDTO }
        | { content: ContentOpcDTO }
        | { car: CarOpcDTO }
        | { travel: TravelOpcDTO }
        | { adultAccident: AdultAccidentOpcDTO }
        | undefined
    > = {
        '': undefined,
        prcGoldSilverJewelrySums: undefined,
        dogClub: { dog: { club: attributeValue as DogClub_Top } },
        dogChipId: { dog: { chipId: attributeValue } },
        catChipId: { cat: { chipId: attributeValue } },
        prcVoluntaryAlarm: { content: { voluntaryAlarm: attributeValue as YesNo_Top } },
        carOdometer: { car: { odometerReading: attributeValue } },
        traNumberInHousehold: {
            travel: { moreThanOnePersonInHousehold: attributeValue as YesNo_Top }
        },
        traAreaOfDestination: {
            travel: { areaOfDestination: attributeValue as AreaOfDestination_Top }
        },
        adultAccEmployment: { adultAccident: { employmentCode: attributeValue } },
        adultAccFulltimeLeisure: {
            adultAccident: { fulltimeOrLeisure: attributeValue as ACCFulltimeOrLeisure_Top }
        },
        adultAccContractType: {
            adultAccident: { contractType: attributeValue as ACCContractType_Top }
        },
        carYearlyMileage: {
            car: {
                yearlyMileage: attributeValue as MVPYearlyMileage_Top,
                odometerReading: ''
            }
        }
    };
    return {
        coverableId,
        productToChange: productName,
        ...coverableAttributeMap[attribute ?? '']
    };
};

const getContinueBtnHippoKey = (attribute: string): string => {
    switch (attribute) {
        case 'carOdometer':
            return 'DSS-ENTER-ODOMETER-READING-FIELD-CONTINUE';
        case 'dogClub':
        case 'prcVoluntaryAlarm':
        case 'adultAccFulltimeLeisure':
        case 'adultAccEmployment':
        case 'traNumberInHousehold':
            // Todo: Use hippo label when label id is define in document
            return 'Bekræft ændring';
        case 'catChipId':
        case 'dogChipId':
            return 'Gem';
        default:
            return 'DSS-COVERABLE-DETAILS-ACCEPT';
    }
};

const getBackBtnHippoKey = (attribute: string): string => {
    switch (attribute) {
        case 'dogClub':
        case 'dogChipId':
        case 'catChipId':
            return 'DSS-COMMON-BACK';
        default:
            return 'DSS-COVERABLE-DETAILS-CANCEL';
    }
};

const EditCoverableAttributeModal = ({
    isOpen,
    handleCloseModal
}: {
    isOpen: boolean;
    handleCloseModal: HandleCloseOPCModal;
}): JSX.Element => {
    const isFullScreen = false;
    const getHippoText = useHippoText();
    const opcInfo = useRecoilValue(opcInfoAtom);
    const isOpcDataFilled = useRecoilValue(isOpcDataFilledAtom);
    const isOpcFetching = useRecoilValue(isOpcFetchingAtom);
    const editCoverableDTO = generateEditCoverableDTO(opcInfo);
    const { updateCoverableAttribute, editCoverablePrice, revertChange, coverableAttribute } =
        useEditCoverablePrice(editCoverableDTO, getEditCoverableKey(opcInfo.productName));
    const { goBackToDetailsPage, goToShoppingBasket } = usePageNavigation();
    const { withdrawCurrentOPCShoppingBasketIfNotExisting } = useOpcJob();
    const { selectedPaymentFrequency, renewalDate } = useRecoilValue(jobAtom);
    const setCallMeVariant = useSetRecoilState(callMeVariantOpcAtom);

    const {
        title,
        AttributeChangeComponent,
        attributeChangeAditionalProps,
        attributeChangeInformationProps
    } = getEditAttributeConfig(opcInfo, getHippoText, editCoverablePrice, selectedPaymentFrequency);
    const editTravelCoverable = editCoverablePrice?.travel?.[0];
    const travelAttribute = editTravelCoverable?.attribute.concat(
        String(editTravelCoverable?.after?.typeKeyValue)
    );
    const isTravelDowngrade =
        editTravelCoverable?.after?.typeKeyDisplayName === 'Rejseforsikring til enkeltperson' &&
        editTravelCoverable?.before?.typeKeyDisplayName === 'Rejseforsikring';
    const continueBtnHippoKey = getContinueBtnHippoKey(opcInfo.attribute ?? '');
    const backBtnHippoKey = getBackBtnHippoKey(opcInfo.attribute ?? '');

    const handleBackToDAShoppingBasket = async (): Promise<void> => {
        await revertChange();
        goToShoppingBasket(false);
    };

    const handleBackToDetailsPage = async (): Promise<void> => {
        await withdrawCurrentOPCShoppingBasketIfNotExisting();
        goBackToDetailsPage();
    };

    const trackChangedAttributes = (): void => {
        const coverableKey = getEditCoverableKey(opcInfo.productName);
        const firstDiff = editCoverablePrice?.[coverableKey]?.[0];

        if (!firstDiff || !coverableAttribute) return;

        const attribut = firstDiff.attribute;
        const displayName = firstDiff.after?.typeKeyDisplayName ?? firstDiff.after?.stringValue;
        const attributValue = firstDiff.after?.typeKeyValue;
        const remainingInputs: OpcCoverableAttribute[] = [];

        Object.entries(coverableAttribute).forEach(([key, value]) => {
            if (value !== attributValue) {
                remainingInputs.push({ [key]: value });
            }
        });

        track(`${coverableKey}EditAttribute`, [
            {
                newYearlyPrice: editCoverablePrice?.newPrice?.yearlyTotal.amount
            },
            {
                newMonthlyPrice: editCoverablePrice?.newPrice?.monthlyTotal.amount
            },
            { [attribut]: displayName },
            ...remainingInputs
        ]);
    };

    const closeCallback = opcInfo.isFromDAFlow
        ? handleBackToDAShoppingBasket
        : handleBackToDetailsPage;

    return (
        <ModalOverlay isOpen={isOpen} isFullScreen={isFullScreen}>
            <ModalDialog
                isFullScreen={isFullScreen}
                closeIconHandler={() => {
                    if (isOpcFetching) return;
                    handleCloseModal(closeCallback);
                }}
            >
                <ModalHeader title={getHippoText(title)} />
                <ModalBody>
                    <AttributeChangeComponent
                        updateCoverableAttribute={updateCoverableAttribute}
                        additionalProps={attributeChangeAditionalProps}
                    />
                    {isOpcFetching ? (
                        <LoadingDots centered />
                    ) : attributeChangeInformationProps ? (
                        <>
                            <AttributeChangeInformationComponent
                                attributeChangeProps={attributeChangeInformationProps}
                            />
                            <HighlightInfoForCoverage
                                coverageId={travelAttribute ?? ''}
                                editCoverageType="add"
                                renewalDate={renewalDate}
                            />
                        </>
                    ) : (
                        <></>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        disabled={!isOpcDataFilled || isOpcFetching}
                        onClick={() => {
                            if (isTravelDowngrade) {
                                setCallMeVariant('traNumberInHousehold');
                                return;
                            }
                            trackChangedAttributes();
                            handleCloseModal(goToShoppingBasket, 'goToShoppingBasket');
                        }}
                    >
                        {getHippoText(continueBtnHippoKey)}
                    </Button>
                    <Button
                        disabled={isOpcFetching}
                        theme="secondary"
                        onClick={() => {
                            handleCloseModal(closeCallback);
                        }}
                    >
                        {getHippoText(backBtnHippoKey)}
                    </Button>
                </ModalFooter>
            </ModalDialog>
        </ModalOverlay>
    );
};

export default EditCoverableAttributeModal;
